import { useState } from "react";
import { Button } from "@/components/ui/button";
import { ArrowRight, PlayCircle } from "lucide-react";
import { HowItWorksDialog } from "./HowItWorksDialog";
import { setFlag } from "@/lib/onboarding";

type Props = {
  /**
   * Called when the user taps Get Started or Try it now. The parent should
   * dismiss the welcome takeover and scroll to / focus the capture surface.
   */
  onGetStarted: () => void;
};

/**
 * Full-bleed welcome takeover for first-time anonymous visitors. Sits above
 * the capture surface in the same scroll container, so dismissing it just
 * reveals the existing landing page below.
 */
export const WelcomeHero = ({ onGetStarted }: Props) => {
  const [howOpen, setHowOpen] = useState(false);

  const start = () => {
    setFlag("onboarded");
    onGetStarted();
  };

  return (
    <section className="relative isolate overflow-hidden">
      <div className="mx-auto flex min-h-[calc(100svh-4rem)] max-w-3xl flex-col items-center justify-start px-6 pt-8 pb-12 text-center sm:justify-center sm:py-16">
        <p className="mb-4 inline-flex items-center gap-1.5 rounded-full bg-mustard/20 px-3 py-1 text-xs font-semibold tracking-wide text-foreground ring-1 ring-mustard/40 animate-fade-up">
          <span className="h-1.5 w-1.5 rounded-full bg-mustard" />
          Welcome to CleanPlate
        </p>
        <h1
          className="font-display text-4xl font-semibold leading-[1.05] text-foreground sm:text-5xl md:text-6xl animate-fade-up"
          style={{ animationDelay: "80ms" }}
        >
          Turn recipe chaos into a&nbsp;clean, organised kitchen
        </h1>
        <p
          className="mx-auto mt-5 max-w-xl text-base text-muted-foreground sm:text-lg animate-fade-up"
          style={{ animationDelay: "160ms" }}
        >
          Capture recipes from photos, links or text — then plan meals & shop
          in minutes.
        </p>
        <div
          className="mt-8 flex flex-col items-stretch justify-center gap-3 sm:flex-row sm:items-center animate-fade-up"
          style={{ animationDelay: "240ms" }}
        >
          <Button size="lg" className="h-12 px-8" onClick={start}>
            Get Started
            <ArrowRight className="h-4 w-4" />
          </Button>
          <Button
            size="lg"
            variant="outline"
            className="h-12 px-8"
            onClick={() => setHowOpen(true)}
          >
            <PlayCircle className="h-4 w-4" />
            See how it works
          </Button>
        </div>
        <p
          className="mt-6 text-xs text-muted-foreground animate-fade-up"
          style={{ animationDelay: "320ms" }}
        >
          No sign-up required to try it.
        </p>
      </div>

      <HowItWorksDialog
        open={howOpen}
        onOpenChange={setHowOpen}
        onTryItNow={start}
      />
    </section>
  );
};
